import {
  AcceptInviteUrl,
  ComplianceOverviewUrl,
  DashboardUrl,
  IncorrectAppMotusUrl,
  NewExperienceUrl,
  TeamComplianceUrl,
  TeamDashboardUrl
} from 'constants/RouteUrls'
import useAuth from 'core/hooks/auth/useAuth'
import useNavigation from 'core/hooks/navigation/useNavigation'
import { useMemo } from 'react'
import { useSettings } from 'core/hooks/settings/useSettings'
import { UserProfile } from 'types/user'

const useLogin = () => {
  const { logout } = useAuth()
  const { data: settings } = useSettings()
  const { push, replace, search } = useNavigation()

  const atPurpleDash = useMemo(() => {
    const currentHost = window.location.host
    const purpleHost = new URL(import.meta.env.VITE_APP_PURPLE_DASHBOARD_URL)
      .host
    return (
      currentHost === purpleHost ||
      settings.featureFlags.ENABLE_DRIVER_EXPERIENCE
    )
  }, [settings.featureFlags.ENABLE_DRIVER_EXPERIENCE])

  /**
   * Handle Motus login redirects based on user converted status
   * @param params User and its converted status
   */
  const handleMotusLogin = ({
    isConverted,
    user
  }: {
    isConverted: boolean
    user: UserProfile
  }) => {
    if (isConverted === undefined) {
      isConverted = !!user.teams?.[0]?.converted_to_new_app
    }

    // If user is converted and login at classic dashboard
    if (isConverted && !atPurpleDash) {
      logout({
        event: '[Purple] Redirect Logout - Not converted and at purple',
        redirect: false
      })
      push(NewExperienceUrl)
      return
    }

    // If user isn't converted and login at purple dashboard
    if (!isConverted && atPurpleDash) {
      logout({
        event: '[Purple] Redirect Logout - Not converted and at purple',
        redirect: false
      })
      push(IncorrectAppMotusUrl, {
        userId: user.token_id,
        authToken: undefined
      })
      return
    }

    push(DashboardUrl)
  }

  /**
   * Handle Everlance login redirects based on user converted status
   * @param params User
   */
  const handleEverlanceLogin = ({ user }: { user: UserProfile }) => {
    const newUrlParams = new URLSearchParams(search)
    const redirectUrl = newUrlParams.get('redirect_url')
    newUrlParams.delete('redirect_url')
    const newSearch = newUrlParams.toString()

    if (!!user?.received_invites?.length && user?.teams?.length === 0) {
      replace(AcceptInviteUrl)

      return
    }

    if (user.teams?.[0]?.roles?.[0]?.name === 'Reviewer') {
      replace(TeamComplianceUrl)

      return
    }
    if (user.teams?.[0]?.roles?.length && !redirectUrl) {
      replace(TeamDashboardUrl)
      return
    }

    if (redirectUrl === '/account/compliance') {
      replace(`${ComplianceOverviewUrl}${newSearch ? `?${newSearch}` : ''}`)
      return
    }

    replace(redirectUrl ?? DashboardUrl)
  }

  return {
    handleMotusLogin,
    handleEverlanceLogin
  }
}

export default useLogin
